import React, { useState, useRef, useEffect } from "react";
import { Typography, Tag, Space, Empty, Card, Button } from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

const { Text } = Typography;

export default function Archive({ groups = [] }) {
  const nav = useNavigate();
  const [activeMonthIndex, setActiveMonthIndex] = useState(0);
  const containerRef = useRef(null);

  // 自动滚动到活动月份 - 移到最顶层
  useEffect(() => {
    if (containerRef.current) {
      const cardWidth = 300;
      const scrollPosition = activeMonthIndex * (cardWidth + 20) - (window.innerWidth / 2 - cardWidth / 2);
      containerRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  }, [activeMonthIndex]);

  // 确保groups是数组且不为空
  if (!Array.isArray(groups) || groups.length === 0) {
    return (
      <div className="card-glow" style={{ 
        padding: 32,
        width: "100%",
        maxWidth: "1200px",
        margin: "0 auto" 
      }}>
        <Empty description="暂无归档数据" />
      </div>
    );
  }

  // 按年份排序（降序）
  const sortedGroups = [...groups].sort((a, b) => {
    return new Date(b.items[0].createdAt) - new Date(a.items[0].createdAt);
  });

  // 处理滚动
  const scroll = (direction) => {
    if (direction === 'left' && activeMonthIndex > 0) {
      setActiveMonthIndex(activeMonthIndex - 1);
    } else if (direction === 'right' && activeMonthIndex < sortedGroups.length - 1) {
      setActiveMonthIndex(activeMonthIndex + 1);
    }
  };

  return (
    <div className="card-glow archive-container" style={{ 
      padding: 32,
      width: "100%",
      maxWidth: "1200px",
      margin: "0 auto",
      overflow: "hidden"
    }}>
      {/* 标题 */}
      <div style={{ 
        textAlign: "center", 
        marginBottom: "40px",
        perspective: "1000px"
      }}>
        <h1 style={{ 
          color: "var(--theme-primary)",
          fontSize: "42px",
          fontWeight: "bold",
          transformStyle: "preserve-3d",
          transform: "rotateX(10deg)",
          textShadow: "0 10px 20px rgba(0,0,0,0.2)"
        }}>
          光阴之海
        </h1>
      </div>

      {/* 控制按钮 */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        marginBottom: "30px",
        position: "relative",
        zIndex: 10
      }}>
        <Button 
          icon={<LeftOutlined />} 
          onClick={() => scroll('left')}
          disabled={activeMonthIndex === 0}
          style={{
            backgroundColor: 'var(--theme-card-bg)',
            border: '1px solid var(--theme-card-border)',
            color: 'var(--theme-text)'
          }}
        >
          上一月
        </Button>
        
        <div style={{
          fontSize: "18px",
          fontWeight: "bold",
          color: "var(--theme-primary)"
        }}>
          {sortedGroups[activeMonthIndex]?.month}
        </div>
        
        <Button 
          icon={<RightOutlined />} 
          onClick={() => scroll('right')}
          disabled={activeMonthIndex === sortedGroups.length - 1}
          style={{
            backgroundColor: 'var(--theme-card-bg)',
            border: '1px solid var(--theme-card-border)',
            color: 'var(--theme-text)'
          }}
        >
          下一月
        </Button>
      </div>

      {/* 3D 时间隧道 */}
      <div 
        ref={containerRef}
        style={{
          display: "flex",
          gap: "20px",
          overflowX: "auto",
          padding: "40px 20px",
          perspective: "1000px",
          scrollBehavior: "smooth",
          scrollbarWidth: "none",
          "-ms-overflow-style": "none"
        }}
      >
        {sortedGroups.map((g, index) => (
          <div 
            key={g.month}
            style={{
              flex: "0 0 300px",
              transformStyle: "preserve-3d",
              transform: `
                rotateY(${Math.min(Math.abs(index - activeMonthIndex) * 15, 45)}deg) 
                scale(${1 - Math.min(Math.abs(index - activeMonthIndex) * 0.1, 0.2)})
              `,
              transition: "all 0.5s ease",
              opacity: 1 - Math.min(Math.abs(index - activeMonthIndex) * 0.15, 0.5)
            }}
          >
            {/* 月份标签 */}
            <div style={{
              backgroundColor: "var(--theme-primary)",
              color: "white",
              padding: "10px",
              borderRadius: "8px 8px 0 0",
              textAlign: "center",
              fontWeight: "bold",
              transform: "translateZ(20px)",
              boxShadow: "0 5px 15px rgba(0,0,0,0.2)"
            }}>
              {g.month} ({g.items.length} 篇)
            </div>
            
            {/* 文章卡片 */}
            <div style={{
              background: "var(--theme-card-bg)",
              border: "1px solid var(--theme-card-border)",
              borderRadius: "0 0 8px 8px",
              padding: "20px",
              transform: "translateZ(10px)",
              boxShadow: "0 10px 25px rgba(0,0,0,0.15)"
            }}>
              <div style={{ 
                display: "flex", 
                flexDirection: "column", 
                gap: "15px",
                maxHeight: "400px",
                overflowY: "auto"
              }}>
                {g.items.map((p) => (
                  <Card
                    key={p.id}
                    hoverable
                    style={{
                      backgroundColor: "var(--theme-card-bg)",
                      border: "1px solid var(--theme-card-border)",
                      borderRadius: "8px",
                      transition: "all 0.3s ease",
                      transform: "translateZ(0)",
                      cursor: "pointer"
                    }}
                    onClick={() => nav(`/post/${p.id}`)}
                  >
                    <div className="archive-item-title" style={{ 
                      fontWeight: 500, 
                      marginBottom: 8, 
                      fontSize: '14px',
                      color: 'var(--theme-text)'
                    }}>
                      {p.title}
                    </div>
                    <Space size={4} wrap style={{ marginBottom: 8 }}>
                      {(p.tags || []).slice(0, 2).map((t) => (
                        <Tag key={t} className="archive-tag" size="small">{t}</Tag>
                      ))}
                    </Space>
                    <Text className="archive-date" style={{ opacity: 0.6, fontSize: '12px' }}>
                      {dayjs(p.createdAt).format("YYYY-MM-DD")}
                    </Text>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* 提示文字 */}
      <div style={{ 
        textAlign: "center", 
        marginTop: "40px",
        color: "var(--theme-text)",
        opacity: 0.6
      }}>
        <p>💡 提示：点击月份卡片查看详情，或使用左右按钮导航</p>
      </div>
    </div>
  );
}